/* src/components/ProtectedRoute.jsx
 * Route protection component - requires email verification and active subscription/trial
 * When trial has ended (0 days left), only /billing is allowed; leads and all other routes redirect to billing.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOnboardingGate } from '../contexts/OnboardingGateContext';
import { isAdmin } from '../utils/admin';

function getTrialEndMs(trialEndDate) {
  if (!trialEndDate) return null;
  if (typeof trialEndDate.toDate === 'function') return trialEndDate.toDate().getTime();
  if (trialEndDate.seconds != null || trialEndDate._seconds != null) {
    const sec = trialEndDate.seconds ?? trialEndDate._seconds;
    return sec * 1000;
  }
  if (typeof trialEndDate === 'string' || typeof trialEndDate === 'number') return new Date(trialEndDate).getTime();
  return null;
}

/** True if user is on trial but trial end date has passed (0 or negative days left). */
function isTrialEnded(userProfile) {
  // Admin override or active/trialing subscription (with or without subscriptionId)
  if (userProfile?.subscriptionStatus === 'active' || userProfile?.subscriptionStatus === 'trialing') return false;
  // Past due: during grace period, treat as having access (backend has full logic)
  if (userProfile?.subscriptionStatus === 'past_due' && userProfile?.paymentFailedAt) {
    const failedMs = getTrialEndMs(userProfile.paymentFailedAt) ?? 0;
    const graceEnd = failedMs + 14 * 24 * 60 * 60 * 1000;
    if (Date.now() < graceEnd) return false;
  }
  if (!userProfile?.trialActive) return false;
  const endMs = getTrialEndMs(userProfile?.trialEndDate);
  if (endMs == null) return false;
  return Date.now() >= endMs;
}

const ONBOARDING_ALLOW = new Set(['/onboarding', '/verify-email', '/billing', '/accept-invite']);

export function ProtectedRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();
  const { needsOnboarding, onboardingGateReady } = useOnboardingGate();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check email verification
  if (location.pathname === '/accept-invite') {
    return children;
  }

  if (!currentUser.emailVerified && location.pathname !== '/verify-email') {
    return <Navigate to="/verify-email" replace />;
  }

  // Always allow billing
  if (location.pathname === '/billing') {
    return children;
  }

  if (location.pathname === '/onboarding') {
    return children;
  }

  // Admins have full access regardless of trial/subscription
  if (isAdmin(currentUser, userProfile)) {
    return children;
  }

  if (
    onboardingGateReady &&
    needsOnboarding &&
    !ONBOARDING_ALLOW.has(location.pathname)
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // When trial has ended (no active subscription), block leads and all other routes — only billing allowed
  const trialEnded = isTrialEnded(userProfile);
  if (trialEnded) {
    return <Navigate to="/billing?trial_expired=1" replace />;
  }

  // Leads allowed when trial/subscription is active
  if (location.pathname === '/leads' || location.pathname.startsWith('/leads/')) {
    return children;
  }

  // Check subscription/trial status for all other routes
  const pastDueInGrace =
    userProfile?.subscriptionStatus === 'past_due' &&
    userProfile?.paymentFailedAt &&
    (() => {
      const failedMs = getTrialEndMs(userProfile.paymentFailedAt) ?? 0;
      return Date.now() < failedMs + 14 * 24 * 60 * 60 * 1000;
    })();
  const hasActiveAccess =
    userProfile?.trialActive === true ||
    userProfile?.subscriptionStatus === 'active' ||
    userProfile?.subscriptionStatus === 'trialing' ||
    pastDueInGrace;

  if (!hasActiveAccess) {
    return <Navigate to="/billing?checkout=true" replace />;
  }

  return children;
}
